import { addAlert } from '../alerts.js';
import { initializeSelect } from '../manuals/form.js';
import { saveWindowState } from '../windows.js';
import { sendPostRequest, sendPatchRequest } from '../utils.js';

const offerPopup = {
    config: {
        endpoints: {
            getAvailableComponents:
                '/api/woocommerce/components/available-for-offer/',
            createOffer: '/api/woocommerce/offers/create/',
            getOfferDetails: (id) => `/api/woocommerce/offers/${id}/`,
            updateOffer: (id) => `/api/woocommerce/offers/${id}/`,
            getComponentDetails: (id) => `/api/components/${id}/`,
            getComponentAttributes: (id) => `/api/components/${id}/attributes/`,
        },
    },

    // --- DOM Elements ---
    form: null,
    modalContainer: null,
    componentSelect: null,
    photosPreview: null,
    componentAttributesContainer: null,
    submitBtn: null,
    btnText: null,
    btnLoader: null,
    titleElement: null,
    titleInput: null,
    managePhotosBtn: null,
    previewInShopBtn: null,
    photosModal: null,
    photosGalleryEl: null,

    // --- State ---
    choicesInstance: null,
    currentComponent: null,
    hasMainPhoto: false,

    init() {
        this.form = document.getElementById('createOfferWindow');
        if (!this.form) return;

        this._cacheDOMElements();
        this._initializeChoices();
        this._bindEvents();

        const savedStateRaw = localStorage.getItem(
            `windowState-${this.form.id}`,
        );
        if (savedStateRaw) {
            try {
                const savedState = JSON.parse(savedStateRaw);
                if (savedState.open) {
                    this.openInCreateMode();
                }
            } catch (error) {
                console.error('Failed to parse saved window state:', error);
            }
        }
    },

    _cacheDOMElements() {
        this.modalContainer = this.form.closest('.window-modal');
        this.componentSelect = this.form.querySelector('#id_component');
        this.photosPreview = document.getElementById('offerPhotosPreview');
        this.componentAttributesContainer = document.getElementById(
            'componentAttributesContainer',
        );
        this.submitBtn = this.form.querySelector('button[type="submit"]');
        this.btnText = this.submitBtn.querySelector('.btn-text');
        this.btnLoader = this.submitBtn.querySelector('.btn-loader');
        this.titleElement = this.form.querySelector('.window-title');
        this.titleInput = this.form.querySelector('#id_title');
        this.managePhotosBtn = document.getElementById('managePhotosBtn');
        this.previewInShopBtn = document.getElementById('previewInShopBtn');
        this.photosModal = document.getElementById('photosWindow');
        if (this.photosModal) {
            this.photosGalleryEl =
                this.photosModal.querySelector('.file-gallery');
        }
    },

    _initializeChoices() {
        initializeSelect(this.componentSelect, {
            searchPlaceholderValue: gettext('Wyszukaj podzespół...'),
            itemSelectText: gettext('Wybierz'),
        });
        this.choicesInstance = this.componentSelect._choices;
    },

    _bindEvents() {
        this.form.addEventListener('submit', (e) => this.handleFormSubmit(e));
        this.componentSelect.addEventListener('change', (e) => {
            this.fetchComponentData(e.target.value);
            this._validateFields();
        });
        this.titleInput.addEventListener('input', () => {
            this._updateSubmitButtonState();
            this._validateFields();
        });

        const createBtn = document.querySelector(
            '.window-toggle[data-window="createOfferWindow"]',
        );
        if (createBtn) {
            createBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.openInCreateMode();
            });
        }

        const closeButtons = this.form.querySelectorAll('.window-close');
        closeButtons.forEach((btn) =>
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.closeAndReset();
            }),
        );

        document.addEventListener('photosUpdatedInModal', () => {
            if (this.currentComponent) {
                this._refreshComponentData();
            }
        });
    },

    _validateFields() {
        const titleField = this.titleInput.closest('.form-field');
        if (titleField && titleField.classList.contains('indicator-red')) {
            if (this.titleInput.value.trim().length > 0) {
                titleField.classList.add('is-valid');
            } else {
                titleField.classList.remove('is-valid');
            }
        }

        const componentField = this.componentSelect.closest('.form-field');
        if (
            componentField &&
            componentField.classList.contains('indicator-red')
        ) {
            if (this.componentSelect.value) {
                componentField.classList.add('is-valid');
            } else {
                componentField.classList.remove('is-valid');
            }
        }
    },

    async openInCreateMode(componentIdToSelect = null) {
        this.resetModal();
        this._configureForCreate();
        this._showModal();
        this._updateSubmitButtonState();
        this._updateFooterButtonsState();
        this._validateFields();

        try {
            const response = await fetch(
                this.config.endpoints.getAvailableComponents,
            );
            if (!response.ok)
                throw new Error('Failed to fetch available components');
            const components = await response.json();
            const choices = components.map((comp) => ({
                value: String(comp.id),
                label: comp.enova_id,
            }));

            this.choicesInstance.setChoices(
                [
                    {
                        value: '',
                        label: '---------',
                        selected: true,
                        disabled: true,
                    },
                    ...choices,
                ],
                'value',
                'label',
                true,
            );

            if (componentIdToSelect && this.choicesInstance) {
                this.choicesInstance.setChoiceByValue(
                    String(componentIdToSelect),
                );
                this.fetchComponentData(componentIdToSelect);
                this._validateFields();
            }
        } catch (error) {
            console.error('Error in openInCreateMode:', error);
            addAlert(
                'danger',
                gettext(
                    'Nie udało się załadować listy dostępnych podzespołów.',
                ),
            );
        }
    },

    async openInEditMode(offerId) {
        this.resetModal();
        this._configureForEdit(offerId);

        try {
            const response = await fetch(
                this.config.endpoints.getOfferDetails(offerId),
            );
            if (!response.ok) throw new Error('Failed to fetch');
            const offerData = await response.json();

            await this.fetchComponentData(offerData.component.id);
            this._populateForm(offerData);
            this._updateFooterButtonsState(offerData);
            this._validateFields();
        } catch (error) {
            console.error('Error opening edit mode:', error);
            addAlert(
                'danger',
                gettext('Nie udało się załadować danych oferty do edycji.'),
            );
            return;
        }

        this._showModal();
        this._updateSubmitButtonState();
    },

    async handleFormSubmit(event) {
        event.preventDefault();
        this._setLoadingState(true);

        const mode = this.form.dataset.mode;
        const offerId = this.form.dataset.offerId;
        const payload = this._buildPayload(mode);
        let isSuccess = false;

        try {
            if (mode === 'create') {
                await sendPostRequest(
                    this.config.endpoints.createOffer,
                    payload,
                );
                addAlert(
                    'success',
                    gettext(
                        'Nowa oferta została pomyślnie utworzona i zsynchronizowana.',
                    ),
                );
            } else {
                await sendPatchRequest(
                    this.config.endpoints.updateOffer(offerId),
                    payload,
                );
                addAlert(
                    'success',
                    gettext('Oferta została pomyślnie zaktualizowana.'),
                );
            }
            isSuccess = true;
        } catch (error) {
            console.error(`Error during offer ${mode}:`, error);
            addAlert(
                'error',
                error.message || gettext('Wystąpił nieoczekiwany błąd.'),
            );
        } finally {
            this._notifyOfferUpdated();

            if (isSuccess) {
                this.closeAndReset();
            } else {
                this._setLoadingState(false);
                this._updateSubmitButtonState();
            }
        }
    },

    async fetchComponentData(componentId) {
        this.photosPreview.classList.remove('error');
        this.photosPreview.classList.remove('has-photos');
        this.photosPreview.innerHTML = `<p>${gettext('Ładowanie zdjęć...')}</p>`;
        this.componentAttributesContainer.innerHTML = `<div class="no-data-placeholder">${gettext('Ładowanie atrybutów...')}</div>`;

        this.currentComponent = null;
        this.hasMainPhoto = false;
        this._updatePhotoManagementState();
        this._updateFooterButtonsState();

        if (!componentId) {
            this.resetComponentData();
            return;
        }

        try {
            const [componentResponse, attributesResponse] = await Promise.all([
                fetch(this.config.endpoints.getComponentDetails(componentId)),
                fetch(
                    this.config.endpoints.getComponentAttributes(componentId),
                ),
            ]);

            if (!componentResponse.ok)
                throw new Error('Failed to fetch component details');
            if (!attributesResponse.ok)
                throw new Error('Failed to fetch component attributes');

            const component = await componentResponse.json();
            const attributesByGroup = await attributesResponse.json();

            this.currentComponent = component;
            this.displayPhotos(component);
            this.displayComponentAttributes(attributesByGroup);
            this._updatePhotoManagementState();
            this._updateFooterButtonsState();
        } catch (error) {
            console.error('Error fetching component data:', error);
            this.resetComponentData();
            addAlert(
                'error',
                gettext('Błąd podczas ładowania danych podzespołu.'),
            );
        }
    },

    async _refreshComponentData() {
        if (!this.currentComponent) return;

        this.photosPreview.classList.remove('error');
        this.photosPreview.classList.remove('has-photos');
        this.photosPreview.innerHTML = `<p>${gettext('Ładowanie zdjęć...')}</p>`;
        this.componentAttributesContainer.innerHTML = `<div class="no-data-placeholder">${gettext('Ładowanie atrybutów...')}</div>`;

        try {
            const [componentResponse, attributesResponse] = await Promise.all([
                fetch(
                    this.config.endpoints.getComponentDetails(
                        this.currentComponent.id,
                    ),
                ),
                fetch(
                    this.config.endpoints.getComponentAttributes(
                        this.currentComponent.id,
                    ),
                ),
            ]);

            if (!componentResponse.ok)
                throw new Error('Failed to refresh component details');
            if (!attributesResponse.ok)
                throw new Error('Failed to refresh component attributes');

            const component = await componentResponse.json();
            const attributesByGroup = await attributesResponse.json();

            this.currentComponent = component;
            this.displayPhotos(component);
            this.displayComponentAttributes(attributesByGroup);
        } catch (error) {
            console.error('Error refreshing component data:', error);
            this.resetComponentData();
            addAlert(
                'error',
                gettext('Błąd podczas odświeżania danych podzespołu.'),
            );
        }
    },

    displayPhotos(component) {
        this.photosPreview.innerHTML = '';
        this.photosPreview.classList.remove('error');
        this.photosPreview.classList.remove('has-photos');

        if (!component) {
            this.photosPreview.innerHTML = `<p class="no-photos-placeholder">${gettext('Błąd ładowania danych zdjęć.')}</p>`;
            this.hasMainPhoto = false;
            this._updateSubmitButtonState();
            return;
        }

        this.hasMainPhoto =
            component.photos && component.photos.some((p) => p.is_main);

        if (!this.hasMainPhoto) {
            this.photosPreview.classList.add('error');
            this.photosPreview.innerHTML = `
                <div class="no-photos-placeholder">
                    <strong>${gettext('Brak zdjęć')}</strong>
                    <span>${gettext('Ten')} <span class="highlight">${gettext('podzespół')}</span> ${gettext('nie ma wybranego zdjęcia głównego')}</span>
                    <br>
                    <span>${gettext('Dodaj nowe zdjęcia')}</span>
                </div>
            `;
            this._updateSubmitButtonState();
            return;
        }

        const offerPhotos = component.photos.filter(
            (p) => p.is_main || p.is_offer,
        );

        if (offerPhotos.length === 0) {
            this.photosPreview.innerHTML = `<p class="no-photos-placeholder">${gettext('Brak zdjęć ofertowych.')}</p>`;
            this.hasMainPhoto = false;
            this._updateSubmitButtonState();
            return;
        }

        this.photosPreview.classList.add('has-photos');
        this._updateSubmitButtonState();

        offerPhotos
            .sort((a, b) => b.is_main - a.is_main)
            .forEach((photo) => {
                const formattedDate = photo.upload_time
                    ? DateTime.fromISO(photo.upload_time).toFormat(
                          'yyyy.MM.dd HH:mm',
                      )
                    : '';
                const photoCard = document.createElement('div');
                photoCard.className = `photo-card ${photo.is_main ? 'is-main' : ''} ${photo.is_offer ? 'is-offer' : ''}`;
                photoCard.innerHTML = `
                    <div class="photo-status-icons">
                        ${photo.is_main ? `<img src="/static/images/main_offer.svg" alt="Main offer photo" title="Main offer photo">` : ''}
                        ${photo.is_offer && !photo.is_main ? `<img src="/static/images/offer.svg" alt="Offer photo" title="Offer photo">` : ''}
                    </div>
                    <div class="photo-image-wrapper">
                        <img src="${photo.square_thumbnail || '/static/images/pic_fill.svg'}" alt="${photo.name}" class="js-photo-preview-trigger" data-url="${photo.file}" data-filename="${photo.name}">
                    </div>
                    <div class="photo-details">
                        <p class="photo-name">${photo.name}</p>
                        <p class="photo-date">Data: ${formattedDate}</p>
                    </div>
                `;
                this.photosPreview.appendChild(photoCard);
            });

        if (window.tables && window.tables.initializePhotoPreview) {
            window.tables.initializePhotoPreview(this.photosPreview);
        }
    },

    displayComponentAttributes(attributesByGroup) {
        if (!this.componentAttributesContainer) return;
        this.componentAttributesContainer.innerHTML = '';

        if (!attributesByGroup || Object.keys(attributesByGroup).length === 0) {
            this.componentAttributesContainer.innerHTML = `<div class="no-data-placeholder">${gettext('Brak atrybutów do wyświetlenia w sklepie.')}</div>`;
            return;
        }

        const legendOrder = [
            'Ogólne',
            'Parametry techniczne',
            'Gabaryt',
            'Dodatkowe',
            'Uwagi do podzespołu',
        ].map(gettext);

        const fragment = document.createDocumentFragment();

        legendOrder.forEach((legendName) => {
            const attributes = attributesByGroup[legendName];
            if (attributes && attributes.length > 0) {
                const fieldset = document.createElement('fieldset');
                fieldset.className = 'component-card-section';

                const legend = document.createElement('legend');
                legend.textContent = legendName;
                fieldset.appendChild(legend);

                const contentDiv = document.createElement('div');
                contentDiv.className = 'fieldset-content';

                let fieldsHTML = '';
                attributes.forEach((attr) => {
                    const value =
                        attr.value === '' || attr.value === null
                            ? `<span class="no-data">-- Brak --</span>`
                            : attr.unit
                              ? `${attr.value} <span class="unit">${attr.unit}</span>`
                              : attr.value;

                    fieldsHTML += `<div class="card-field"><label>${attr.name}</label><div class="value">${value}</div></div>`;
                });

                contentDiv.innerHTML = fieldsHTML;
                fieldset.appendChild(contentDiv);
                fragment.appendChild(fieldset);
            }
        });

        if (fragment.children.length > 0) {
            this.componentAttributesContainer.appendChild(fragment);
        } else {
            this.componentAttributesContainer.innerHTML = `<div class="no-data-placeholder">${gettext('Brak skonfigurowanych atrybutów dla tego podzespołu.')}</div>`;
        }
    },

    resetComponentData() {
        this.photosPreview.classList.remove('error');
        this.photosPreview.classList.remove('has-photos');
        this.photosPreview.innerHTML = `
            <div class="no-photos-placeholder">
                <strong>${gettext('Brak zdjęć')}</strong>
                <span>${gettext('Wybierz')} <span class="highlight">${gettext('podzespół')}</span>, ${gettext('aby zobaczyć zdjęcia.')}</span>
            </div>
        `;
        this.componentAttributesContainer.innerHTML = `
            <div class="no-data-placeholder">
                ${gettext('Wybierz')} <span class="highlight">${gettext('podzespół')}</span>, ${gettext('aby zobaczyć jego atrybuty.')}
            </div>
        `;
        this.currentComponent = null;
        this.hasMainPhoto = false;
        this._updatePhotoManagementState();
        this._updateSubmitButtonState();
    },

    resetModal() {
        this.form.reset();
        this.form.dataset.mode = '';
        this.form.dataset.offerId = '';

        if (this.choicesInstance) {
            this.choicesInstance.enable();
            this.choicesInstance.clearStore();
            this.choicesInstance.clearInput();
            const searchInput = this.choicesInstance.input.element;
            if (searchInput) searchInput.style.display = 'block';
        }

        this.resetComponentData();
        this._updateFooterButtonsState();
        this._validateFields();
    },

    _updateSubmitButtonState() {
        const isDisabled =
            this.titleInput.value.trim() === '' || !this.hasMainPhoto;
        this.submitBtn.disabled = isDisabled;
        this.submitBtn.classList.toggle('disabled', isDisabled);
    },

    _buildPayload(mode) {
        const priceValue = this.form.querySelector('#id_price').value;

        const componentIdValue = this.componentSelect.value;
        const componentIdInt = componentIdValue
            ? parseInt(componentIdValue, 10)
            : null;

        const data = {
            component_id: mode === 'create' ? componentIdInt : undefined,
            title: this.titleInput.value,
            description: this.form.querySelector('#id_description').value,
            price: priceValue === '' ? null : priceValue,
        };
        Object.keys(data).forEach(
            (key) => data[key] === undefined && delete data[key],
        );
        return data;
    },

    _notifyOfferUpdated() {
        document.dispatchEvent(new Event('offerUpdated'));
    },

    _setLoadingState(isLoading) {
        this.btnText.style.display = isLoading ? 'none' : 'inline';
        this.btnLoader.style.display = isLoading ? 'inline' : 'none';
        this.submitBtn.disabled = isLoading;
        if (isLoading) this.submitBtn.classList.add('disabled');
    },

    _configureForCreate() {
        this.form.dataset.mode = 'create';
        this.titleElement.textContent = gettext('DANE DO SKLEPU - NOWA OFERTA');
        this.btnText.textContent = gettext('AKCEPTUJ I UTWÓRZ OFERTĘ');
        this.choicesInstance.enable();
    },

    _configureForEdit(offerId) {
        this.form.dataset.mode = 'edit';
        this.form.dataset.offerId = offerId;
        this.titleElement.textContent = gettext(
            'DANE DO SKLEPU - EDYCJA OFERTY',
        );
        this.btnText.textContent = gettext('ZAPISZ ZMIANY');
    },

    _populateForm(offerData) {
        this.titleInput.value = offerData.title;
        this.form.querySelector('#id_description').value =
            offerData.description || '';
        this.form.querySelector('#id_price').value = offerData.price || '';

        this.choicesInstance.clearStore();
        this.choicesInstance.setChoices(
            [
                {
                    value: offerData.component.id,
                    label: offerData.component.enova_id,
                    selected: true,
                },
            ],
            'value',
            'label',
            true,
        );
        this.choicesInstance.disable();

        const searchInput = this.choicesInstance.input.element;
        if (searchInput) {
            searchInput.style.display = 'none';
        }
    },

    _showModal() {
        this.modalContainer.classList.add('window-modal-open');
        this.form.classList.add('window-open');
    },

    closeAndReset() {
        this.modalContainer.classList.remove('window-modal-open');
        this.form.classList.remove('window-open');
        saveWindowState(this.form);
        this.resetModal();
        this._setLoadingState(false);
        this._updateSubmitButtonState();
    },

    _updatePhotoManagementState() {
        if (
            this.currentComponent &&
            this.managePhotosBtn &&
            this.photosGalleryEl
        ) {
            const getUrl = `/api/components/${this.currentComponent.id}/`;
            const addUrl = `/api/components/${this.currentComponent.id}/photos/add/`;
            const removeUrl = `/api/components/${this.currentComponent.id}/photos/remove/`;

            this.photosGalleryEl.dataset.getUrl = getUrl;
            this.photosGalleryEl.dataset.addUrl = addUrl;
            this.photosGalleryEl.dataset.removeUrl = removeUrl;

            document.dispatchEvent(new CustomEvent('photosUpdatedInModal'));
        }
    },

    _updateFooterButtonsState(offerData = null) {
        const hasComponent = !!this.currentComponent;

        if (this.managePhotosBtn) {
            this.managePhotosBtn.style.display = 'inline-flex';
            this.managePhotosBtn.disabled = !hasComponent;
            this.managePhotosBtn.classList.toggle('disabled', !hasComponent);
        }

        if (this.previewInShopBtn) {
            const productUrl = offerData?.product_url;
            if (hasComponent && productUrl) {
                this.previewInShopBtn.href = productUrl;
                this.previewInShopBtn.style.display = 'inline-flex';
            } else {
                this.previewInShopBtn.style.display = 'none';
                this.previewInShopBtn.removeAttribute('href');
            }
        }
    },
};

window.offerPopup = offerPopup;

export function initializeCreateOfferPopup() {
    window.offerPopup.init();
}
